import { useEffect, useState } from "react";
import { PoundSterling, CalendarCheck, Users, CalendarClock } from "lucide-react";
import { PageHead } from "@/pages/admin/AdminLayout";
import { api, gbp, longDate, shortDate, isoDate, STATUS_STYLES, statusLabel } from "@/lib/api";

const Stat = ({ icon: I, label, value, testid }) => (
  <div className="rounded-2xl bg-card border border-border p-5" data-testid={testid}>
    <span className="h-9 w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><I className="h-4 w-4" /></span>
    <p className="text-sm text-muted-foreground mt-4">{label}</p>
    <p className="font-display text-2xl font-extrabold mt-1">{value}</p>
  </div>
);

export const BookingLine = ({ b, right, when }) => (
  <div className="flex items-center justify-between gap-3 py-3 border-b border-border last:border-0" data-testid={`admin-booking-row-${b.id}`}>
    <div className="flex items-center gap-4 min-w-0">
      <span className="font-display font-bold text-sm w-28 shrink-0">{when || `${b.start}–${b.end}`}</span>
      <div className="min-w-0">
        <p className="font-medium text-sm truncate">{b.patient_name} · {b.service_name}</p>
        <p className="text-xs text-muted-foreground truncate">{b.practitioner_name} · {gbp(b.amount)}{b.refund_status === "refunded" ? ` · refunded ${gbp(b.refund_amount)}` : b.status === "cancelled" ? " · not refunded" : ""}{b.reminder_sent_at ? " · reminder sent" : ""}</p>
      </div>
    </div>
    {right || <span className={`text-xs capitalize rounded-full border px-2.5 py-1 ${STATUS_STYLES[b.status]}`}>{statusLabel(b.status)}</span>}
  </div>
);

export default function Overview() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/overview").then((r) => setD(r.data)); }, []);
  if (!d) return null;
  return (
    <div className="fade-up">
      <PageHead eyebrow={longDate(isoDate(new Date()))} title="Overview" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={PoundSterling} label="Revenue this month" value={gbp(d.month_revenue)} testid="stat-month-revenue" />
        <Stat icon={CalendarCheck} label="Bookings this month" value={d.month_bookings} testid="stat-month-bookings" />
        <Stat icon={CalendarClock} label="Upcoming appointments" value={d.upcoming_count} testid="stat-upcoming" />
        <Stat icon={Users} label="Patients" value={d.patient_count} testid="stat-patients" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <div className="rounded-2xl bg-card border border-border p-5" data-testid="overview-today">
          <p className="font-display font-bold mb-2">Today's schedule</p>
          {d.today.length === 0 ? <p className="text-sm text-muted-foreground py-4">No appointments today.</p> : d.today.map((b) => <BookingLine key={b.id} b={b} />)}
        </div>
        <div className="rounded-2xl bg-card border border-border p-5" data-testid="overview-recent">
          <p className="font-display font-bold mb-2">Latest bookings</p>
          {d.recent.length === 0 ? <p className="text-sm text-muted-foreground py-4">No bookings yet.</p> : d.recent.map((b) => (
            <BookingLine key={b.id} b={b} when={`${shortDate(b.date)} · ${b.start}`} />
          ))}
        </div>
      </div>
    </div>
  );
}
